import { WEB_VIEW } from 'shared/src/models/auth';
import { DESTINATION_APP_TYPE, MESSAGE_TYPE } from 'shared/src/composables/messages/messages.model';
import { UseMessages, useMessages } from 'shared/src/composables/messages/useMessages.composable';
import { INITIAL_LANGUAGE } from 'shared/src/modules/languageModule/consts';
import { useLanguage } from 'shared/src/composables/useLanguage.composable';
import { sendExceptionEvent } from 'shared/src/utils/appInsights';
import { SeverityLevel } from '@microsoft/applicationinsights-common';

import { setLanguage } from '../language/langauge.service';
import { showBannerText, showLoaderText } from '../helpers';
import { loadIncorrectPerson, loadOrganization, loadUser } from '../loaders';
import MobileAuthorizationService from './mobileAuthorization';
import StandardAuthorizationService from './standardAuthorization';
import { AuthorizationResult } from './types';


const { storeLanguage } = useLanguage();

class AuthorizationStrategy {
  async handleAuthorizationResult(result: AuthorizationResult) {

    if (result.profile?.orgid) {
      loadOrganization();
    } else {
      try {
        // loadIncorrectPerson("NotAdult");
        loadUser();
      } catch (e) {
        console.log(e);
      }
    }
  }
}

export class MobileAuthorizationStrategy extends AuthorizationStrategy {
  private useMessages: UseMessages;
  private mobileAuthService: MobileAuthorizationService;

  constructor() {
    super();
    this.mobileAuthService = new MobileAuthorizationService();
    this.useMessages = useMessages();
    this.useMessages.startMessageListener();
  }

  async authorize(): Promise<void | null> {
    this.useMessages.addOnMessageActionAlt(DESTINATION_APP_TYPE.HOST, MESSAGE_TYPE.REFRESH_TOKEN_INJECTION,
    async (refreshToken: string, messageEvent) => {
      if (refreshToken) {
        console.log('REFRESH_TOKEN_INJECTION', messageEvent);
        await this.mobileAuthService.decodeAndSetToken(refreshToken);
      }
    });

    this.useMessages.addOnMessageActionAlt(DESTINATION_APP_TYPE.HOST, MESSAGE_TYPE.LOGOUT_FROM_MOBILE_APP,
    async (messageEvent) => {
      console.log('LOGOUT USER FROM MOBILE APP', messageEvent);
      sessionStorage.clear();
      localStorage.clear();
    });

    const tokenReceived = async () =>  new Promise<string>((resolve, reject) => {
      this.useMessages.addOnMessageActionAlt(DESTINATION_APP_TYPE.HOST, MESSAGE_TYPE.TOKEN_INJECTION,
        async (token: string, messageEvent) => {
          console.log('TOKEN_INJECTION', messageEvent);
          if (token) {
            resolve(token);
          }
        });
    });

    const tokenAndLanguageReceived = async () => new Promise<{ token: string, language: string }>((resolve) => {
      this.useMessages.addOnMessageActionAlt(DESTINATION_APP_TYPE.HOST, MESSAGE_TYPE.TOKEN_AND_LANGUAGE_INJECTION,
        async ({ token, language }, messageEvent) => {
          console.log('TOKEN_AND_LANGUAGE_INJECTION', messageEvent);
          if (token && language) {
            resolve({ token, language });
        } else if (token) {
          resolve ({ token, language: INITIAL_LANGUAGE });
        }
      });
    });

    this.useMessages.postWindowMessage(
      DESTINATION_APP_TYPE.MOBILE,
      MESSAGE_TYPE.WEB_INITIALIZED,
      null
    );

    const tokenOrTokenAndLanguageReceived = async () => {
      return Promise.race([
        tokenReceived().then(token => ({ token, language: INITIAL_LANGUAGE })),
        tokenAndLanguageReceived(),
      ]);
    };

    const { token, language } = await tokenOrTokenAndLanguageReceived();
    sessionStorage.setItem(WEB_VIEW, 'true');
    storeLanguage(language);

    const result = await this.mobileAuthService.decodeAndSetToken(token);
    return result ? this.handleAuthorizationResult(result) : null;
  }
}

export class StandardAuthorizationStrategy extends AuthorizationStrategy {
  private readonly authCode: string | null;
  constructor(authCode: string | null) {
    super();
    this.authCode = authCode;
  }

  async authorize(): Promise<void | null> {
    const authService = new StandardAuthorizationService();

    try {
      const result = await authService.authorize(this.authCode);
      showBannerText(result ? result.profile?.slng : undefined);
      showLoaderText(result ? result.profile?.slng : undefined);

      if (result?.profile) {
        await setLanguage(result.profile);
      }

      return result ? this.handleAuthorizationResult(result) : null;
    } catch (errorCode: any) {
      if (!sessionStorage.getItem('orgId')) {
        loadIncorrectPerson(errorCode);

        return;
      }
      sessionStorage.removeItem('orgId');
      return this.authorize();
    }
  }
}
